import { Fragment, useState } from 'react';

import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Select,
  TextField,
  Typography
} from '@mui/material';

import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';

import axios from 'axios';
import useSWR from 'swr';

import Error from './_error';

import BottomNav from '../components/BottomNav';
import CompactRequestCard from '../components/CompactRequestCard';
import HeroCondensed from '../components/HeroCondensed';
import Link from '../components/Link';
import Paginator from '../components/Paginator';
import PersonalisedEstimate from '../components/PersonalisedEstimate';
import RequestCard from '../components/RequestCard';

import TopNav from '../components/TopNav';
import WaivingBuddy from '../components/WaivingBuddy';

import cars from '../constants/cars';
import jobCategories from '../constants/jobCategories';

import mobileCG from '../public/partners/v2/325x80-compare-guru-insurance.png';
import warrantyCG from '../public/partners/v2/500x200-compare-guru-pre-owned-warranty.png';


const compareKeys = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
}

const fetcher = (url, query) => {
  const {make, jobcategory, page} = query;
  return axios({
    baseURL: process.env.DOMAIN,
    url,
    params: {
      make,
      jobcategory,
      page
    }
  })
  .then(res => res.data);
}

function CostEstimates(props) {
  
  const router = useRouter();

  const initialJobCategory = router.query.jobcategory ? router.query.jobcategory : '';
  const initialMake = router.query.make ? router.query.make : '';
  const [make, setMake] = useState(initialMake);
  const [jobcategory, setJobCategory] = useState(initialJobCategory);

  // show loading if not original request
  const initialQuery = props.query;
  const fallbackData = compareKeys(initialQuery, router.query) ? props.data : undefined;

  // maybe limit this to car & near only instead of entire query
  const { data, error } = useSWR(
    ['/content/jobs/', router.query],
    fetcher,
    { fallbackData }
  );

  if (props.statusCode) {
    return <Error statusCode={props.statusCode} />
  }

  const handleSearch = () => {
    router.push({
      pathname: router.pathname,
      query: { make, jobcategory }
    }, {
      pathname: router.pathname,
      query: { make, jobcategory }
    }, { shallow: true })
  }

  const currentPage = parseInt(router.query.page) || 1;
  const pageUp = parseInt(currentPage) ? currentPage + 1 : 2;
  const pageDown = parseInt(currentPage) > 1 ? currentPage - 1 : null;

  const nextUrl = {
    pathname: router.pathname,
    query: {
      ...router.query,
      page: pageUp
    }
  }

  const previousUrl = {
    pathname: router.pathname,
    query: {
      ...router.query,
      page: pageDown
    }
  }

  const pageTitle = `${initialMake || ''} ${initialJobCategory || ' Quote Requests and'} Cost Estimates`;
  const pageDescription = `View the cost estimates received from mechanics and workshops for ${initialMake || ''} ${initialJobCategory || 'repair and service'} quote requests by others.`

  const headerTitle = `${initialMake || ''} ${initialJobCategory || ''} Cost Estimates`;
  const resultSummary = `Showing ${data && data.count} ${initialMake || ''} ${initialJobCategory ? initialJobCategory : ''} quote requests`

  return <>
    <Head>
      <title>{pageTitle} | MechanicBuddy</title>
      <meta
        name="description"
        content={pageDescription}
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://www.mechanicbuddy.co.za${router.asPath}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary" />
      <meta property="twitter:url" content={`https://www.mechanicbuddy.co.za${router.asPath}`} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg" />

      <link rel="canonical" href={`https://www.mechanicbuddy.co.za${router.asPath}`} />

      {data && data.count === 0 && <meta name="robots" content="noindex" />}
    </Head>

    <TopNav />
    
    <HeroCondensed
      title={headerTitle}
      description="Search through quote requests and find the cost estimates that others received"
      backgroundImage="/img/estimate-bg.jpg"
    />

    <Container>
      <Box bgcolor="white" p={3} my={4}>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} md={5}>
            <FormControl fullWidth variant="outlined">
              <Select
                native
                value={jobcategory}
                onChange={event => setJobCategory(event.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <IconButton edge="start" size="large">
                      <BuildIcon />
                    </IconButton>
                  </InputAdornment>}
              >
                <option aria-label="All Services & Repairs" value="">All Services & Repairs</option>
                {jobCategories.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={5}>
            <Autocomplete
              options={cars}
              value={make}
              autoHighlight={true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Any Car"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <IconButton edge="start" size="large">
                            <DirectionsCarIcon />
                          </IconButton>
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              onChange={(event, value) => {
                setMake(value);
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center'}}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              startIcon={<SearchIcon />}
              onClick={() => handleSearch()}
            >Search</Button>
          </Grid>
        </Grid>
      </Box>

      <Grid
        container
        spacing={3}
        direction="row"
      >
        <Grid item xs={12} md={8}>
          {error && <div>Something went wrong ...</div>}
          {!data && 
            <Box bgcolor="white" pt={2}>
              <Box fontSize="subtitle1.fontSize" component="h4" pl={2} fontWeight={400}>
                Loading ... 
              </Box>
              <Box p={2}>
                <Skeleton variant="rectangular" width="100%" height={160} />
                <Skeleton variant="rectangular" width="100%" height={160} />
                <Skeleton variant="rectangular" width="100%" height={160} />
              </Box>
            </Box>
          }

          {data && data.count > 0 &&
            <Box
              sx={{
                backgroundColor: {
                  xs: "none",
                  md: "white",
                },
                px: {
                  sm: 8,
                  md: 0
                },
                pt: {
                  md: 2
                }
              }}
            >
              <Box fontSize="subtitle1.fontSize" component="h4" pl={2} fontWeight={400}>
                {resultSummary}
              </Box>
              <Box
                sx={{
                  display: {
                    xs: "none",
                    md: "block"
                  }
                }}
              >
                {data.results.map((job, index, arr) => (
                  <Fragment key={job.id}>
                    <RequestCard job={job} />
                    {index != arr.length - 1 && <Divider />}
                  </Fragment>
                ))}
              </Box>

              <Box
                sx={{
                  display: {
                    xs: "block",
                    md: "none"
                  }
                }}
              >
                {data.results.map((job, index, arr) => (
                  <CompactRequestCard key={job.id} job={job} />
                ))}
              </Box>
            </Box>
          }

          {data && data.count === 0 &&
            <Box
              bgcolor="white"
              p={2}
              textAlign="center"
              sx={{
                '& .MuiTypography-root': {
                  mt: 2
                },
                mb: 4
              }}
            >
              <Image
                src="/icons/404.svg"
                width={200}
                height={200}
                alt="End of road"
              />
              <Typography variant="h5">Sorry, no cost estimates available for this search</Typography>
              <Typography>
                <Link
                  color="secondary"
                  href={{ pathname: '/cost-estimates', query: { make: initialMake } }}
                  onClick={() => {setJobCategory('')}}
                >Click here</Link> to view other {initialMake} cost estimates.
              </Typography>
            </Box>
          }

          <Box
            width="100%"
            display={{xs: "flex", md: "none"}}
            flexDirection="row"
            justifyContent="center"
          >
            <a
              href="https://compareguru.co.za/insurance-quotes/car-insurance/personal?channel=mechanic-buddy"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <Image
                src={mobileCG}
                alt="CompareGuru car insurance"
                quality={100}
              />
            </a>
          </Box>

          {data && data.count > 0 &&
            <Paginator
              pageDown={pageDown}
              previousUrl={previousUrl}
              currentPage={currentPage}
              totalPages={data.total_pages}
              nextUrl={nextUrl}
              pageUp={pageUp}
            />
          }
        </Grid>

        <Grid item xs={12} md={4}>
          <Hidden mdDown implementation="css">
            <a
              href="https://compareguru.co.za/insurance-quotes/motorvaps/pre-owned-motor-warranty/personal?channel=mechanic-buddy"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <Image
                src={warrantyCG}
                alt="CompareGuru car insurance"
                quality={100}
              />
            </a>

            <Box p={2} />

            {initialMake && <PersonalisedEstimate
              initialMake={initialMake}
              initialModel={''}
              initialService={''}
            />}
          </Hidden>
        </Grid>
      </Grid>
    </Container>

    <WaivingBuddy />

    <BottomNav />
  </>;
}

export async function getServerSideProps({ query, res }) {
  const {data, statusCode} = await fetcher('/content/jobs/', query)
  .then(data => {
    return {
      data,
      statusCode: null
    }
  })
  .catch(error => {
    return {
      data: null,
      statusCode: error.response.status
    }
  });

  if (statusCode === 404) {
    return {
      notFound: true
    }
  }

  return {
    props: {
      data,
      statusCode,
      query
    }
  }
}

export default CostEstimates
